/**
 * Configurable overflow actions menu for per-item action lists (comments,
 * stories, etc.).
 *
 * Wraps RAC `MenuTrigger` + `BaseMenu` with an `OverflowMenuHorizontal` trigger
 * and an optional tooltip. Each action declares its icon, label, an optional
 * `modal` for confirmation, and an `action` callback invoked with the
 * datastore. Actions can be marked destructive or disabled, and an action that
 * names a `capability` is disabled for a user who doesn't hold it. Used as the
 * building block for the per-feature ActionsOverlay wrappers in editorial-dash
 * stories and question actions.
 */

import { OverflowMenuHorizontal } from '@carbon/icons-react';
import { useTranslation } from 'component/translation';
import { ActionButton } from 'component/ui/action-button';
import { BaseMenu, type BaseMenuProps, MenuItemButton, MenuTrigger } from 'component/ui/base-menu';
import { BaseTooltip } from 'component/ui/base-tooltip';
import { useContext, useRef, useState } from 'react';
import { OverlayTriggerStateContext } from 'react-aria-components';
import { useControlledState } from 'react-stately/useControlledState';
import { useHasCapability } from 'util/admin';
import { type Datastore, useDatastore } from 'util/datastore';
import { type FormatParams, labelToKey, type TLabel, t } from 'util/stdtypes';

export type ActionsMenuAction<T = {}> = {
    label: TLabel;
    formatParams?: FormatParams;
    icon: React.ComponentType<{ size?: number }>;
    destructive?: boolean;
    disabled?: boolean;
    capability?: string;
    modal?: React.ComponentType<{ onClose: () => void; onConfirm: () => void | Promise<void> } & T>;
    action: (args: { datastore: Datastore } & T) => void | Promise<void>;
    testID?: string;
};

export type ActionsMenuProps<T> = {
    actions: ActionsMenuAction<T>[];
    actionArgs?: T;
    defaultOpen?: boolean;
    isNonModal?: boolean;
    isOpen?: boolean;
    onOpenChange?: (isOpen: boolean) => void;
    placement?: BaseMenuProps['placement'];
    testID?: string;
    tooltipLabel?: TLabel;
};

function ActionsMenuItem<T>({
    action,
    actionPending,
    id,
    onAction,
}: {
    action: ActionsMenuAction<T>;
    actionPending: boolean;
    id: string;
    onAction: () => void;
}) {
    const text = useTranslation(action.label, action.formatParams);
    const hasCapability = useHasCapability(action.capability);
    return (
        <MenuItemButton
            destructive={action.destructive}
            icon={action.icon}
            id={id}
            isDisabled={action.disabled || (action.capability !== undefined && !hasCapability) || actionPending}
            onAction={onAction}
            testID={action.testID}
            text={text}
        />
    );
}

/**
 * Trigger button rendered inside `MenuTrigger`. Reads the trigger's open state
 * from `OverlayTriggerStateContext` so the tooltip can disable while the menu
 * is open — avoiding a stacked tooltip-and-menu appearance — without mirroring
 * the open state in local React state. A `useState` + `onOpenChange` mirror
 * silently drifts when a parent uses controlled mode and doesn't update
 * `isOpen` in response to `onOpenChange`; reading the context is the
 * always-correct path because RAC's `useOverlayTriggerState` is the source of
 * truth in both controlled and uncontrolled modes.
 */
function ActionsMenuTriggerButton({ testID, text }: { testID: string; text: string }) {
    const state = useContext(OverlayTriggerStateContext);
    const isMenuOpen = state?.isOpen ?? false;
    return (
        <BaseTooltip isDisabled={isMenuOpen} text={text}>
            <ActionButton aria-label={text} icon={OverflowMenuHorizontal} testID={testID} />
        </BaseTooltip>
    );
}

export function ActionsMenu<T>({
    actions,
    actionArgs,
    defaultOpen,
    isNonModal,
    isOpen,
    onOpenChange,
    placement = 'bottom',
    testID = 'actions-menu',
    tooltipLabel,
}: ActionsMenuProps<T>) {
    const datastore = useDatastore();
    const [modalActionIndex, setModalActionIndex] = useState<number | null>(null);

    // True while an action's async work is in-flight. Siblings disable and the
    // menu stays open until the promise resolves so callers (and Playwright
    // tests) can observe completion by waiting for the popover to disappear
    // instead of racing dangling promises. Ported from main's ActionsOverlay
    // (PR #3995).
    const [actionPending, setActionPending] = useState(false);
    // Ref companion: `handleOpenChange` is invoked synchronously by RAC on
    // outside-click / Escape *before* the `setActionPending(true)` state
    // update commits. Reading from the ref in the close-guard preserves intent
    // across that microtask boundary.
    const actionPendingRef = useRef(false);

    // Controlled-or-uncontrolled open state; the close-guard layers on top so
    // RAC's auto-close is refused while an async action is in-flight.
    const [currentIsOpen, setOpen] = useControlledState(isOpen, defaultOpen ?? false, onOpenChange);

    function handleOpenChange(open: boolean) {
        if (!open && actionPendingRef.current) return;
        setOpen(open);
    }

    function closeMenu() {
        setOpen(false);
    }

    function handleModalClose() {
        setModalActionIndex(null);
    }

    const activeAction = modalActionIndex !== null ? actions[modalActionIndex] : null;
    const ModalComponent = activeAction?.modal;

    async function handleConfirm() {
        await activeAction?.action({ datastore, ...(actionArgs as T) });
        handleModalClose();
    }

    async function handleAction(action: ActionsMenuAction<T>, index: number) {
        if (action.modal) {
            // Modal path: close menu, modal renders as sibling and takes over.
            setModalActionIndex(index);
            closeMenu();
            return;
        }
        const result = action.action({ datastore, ...(actionArgs as T) });
        if (!result || typeof (result as Promise<void>).then !== 'function') {
            // Sync path: close immediately, no pending machinery.
            closeMenu();
            return;
        }
        // Async path: keep menu open until promise resolves, then close.
        actionPendingRef.current = true;
        setActionPending(true);
        try {
            await result;
        } finally {
            actionPendingRef.current = false;
            setActionPending(false);
            closeMenu();
        }
    }

    const tooltipText = useTranslation(tooltipLabel ?? t('question.actions.tooltip', 'More options'));

    return (
        <>
            <MenuTrigger isOpen={currentIsOpen} onOpenChange={handleOpenChange}>
                <ActionsMenuTriggerButton testID={testID} text={tooltipText} />
                <BaseMenu isNonModal={isNonModal} placement={placement}>
                    {actions.map((action, index) => {
                        const key = labelToKey(action.label);
                        return (
                            <ActionsMenuItem
                                action={action}
                                actionPending={actionPending}
                                id={key}
                                key={key}
                                onAction={() => handleAction(action, index)}
                            />
                        );
                    })}
                </BaseMenu>
            </MenuTrigger>
            {ModalComponent && <ModalComponent onClose={handleModalClose} onConfirm={handleConfirm} {...(actionArgs as T)} />}
        </>
    );
}
